import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { clienteName, PIANI, today } from "../lib/format";
import { TextField, SelectField, Field } from "./Field";
import { Button } from "./ui/button";
import { PianoFields, DurataConformita, CommercialeFields, ComponentiPicker, veicoloPlaceholder } from "./GaranziaFields";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const EMPTY = { tipo: "conformita", piano: "", cliente_id: "", veicolo_id: "", data_inizio: today(), durata_mesi: 12, km_inizio: 0, km_limite: 0, massimale: 0, prezzo: 0, franchigia: 0, componenti: [], note: "" };
const NUM = ["durata_mesi", "km_inizio", "km_limite", "massimale", "prezzo", "franchigia"];

const toPayload = (f) => Object.fromEntries(Object.keys(EMPTY).map((k) => [k, NUM.includes(k) ? Number(f[k]) || 0 : f[k]]));

const withPiano = (s, p) => {
  const preset = PIANI[p];
  if (!preset) return { ...s, piano: p };
  return { ...s, piano: p, durata_mesi: preset.durata, massimale: preset.massimale, componenti: preset.componenti };
};

const toggled = (list, c) => (list.includes(c) ? list.filter((x) => x !== c) : [...list, c]);

const TipoToggle = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-2 sm:col-span-2">
    {[["conformita", "Conformità legale", "D.Lgs 206/2005 · 12/24 mesi"], ["commerciale", "Commerciale", "Silver · Gold · Platinum · Personalizzata"]].map(([v, t, s]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`garanzia-tipo-${v}`}
        className={`rounded-md border-2 p-3 text-left transition-colors ${value === v ? "border-[#B08D57] bg-[#F4EDE1]" : "border-border hover:border-slate-400"}`}>
        <div className="font-display font-bold">{t}</div><div className="text-xs text-muted-foreground">{s}</div>
      </button>
    ))}
  </div>
);

export const GaranziaDialog = ({ open, onClose, initial, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [clienti, setClienti] = useState([]);
  const [veicoli, setVeicoli] = useState([]);

  useEffect(() => {
    if (!open) return;
    setF(initial ? { ...EMPTY, ...initial } : EMPTY);
    api.get("/clienti").then((r) => setClienti(r.data));
    api.get("/veicoli").then((r) => setVeicoli(r.data));
  }, [open, initial]);

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const veicoliCliente = useMemo(() => veicoli.filter((v) => v.cliente_id === f.cliente_id), [veicoli, f.cliente_id]);

  const pickVeicolo = (id) => {
    const v = veicoli.find((x) => x.id === id);
    setF((p) => ({ ...p, veicolo_id: id, km_inizio: v?.km || 0, data_inizio: p.data_inizio || v?.data_vendita || today() }));
  };
  const pickPiano = (p) => setF((s) => withPiano(s, p));
  const toggleComp = (c) => setF((p) => ({ ...p, componenti: toggled(p.componenti, c) }));

  const save = async () => {
    try {
      const payload = toPayload(f);
      const r = initial?.id ? await api.put(`/garanzie/${initial.id}`, payload) : await api.post("/garanzie", payload);
      toast.success(`Garanzia ${r.data.numero} salvata`); onSaved(r.data); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const comm = f.tipo === "commerciale";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="garanzia-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{initial?.id ? `Modifica ${initial.numero}` : "Attiva nuova garanzia"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <TipoToggle value={f.tipo} onChange={(v) => setF((p) => ({ ...p, tipo: v, piano: "", durata_mesi: v === "conformita" ? 12 : p.durata_mesi }))} />
          <SelectField label="Cliente *" value={f.cliente_id} onChange={(v) => setF((p) => ({ ...p, cliente_id: v, veicolo_id: "" }))} testId="garanzia-cliente-select"
            options={clienti.map((c) => ({ value: c.id, label: clienteName(c) }))} />
          <SelectField label="Veicolo *" value={f.veicolo_id} onChange={pickVeicolo} testId="garanzia-veicolo-select"
            placeholder={veicoloPlaceholder(f.cliente_id, veicoliCliente.length)}
            options={veicoliCliente.map((v) => ({ value: v.id, label: `${v.targa} · ${v.marca} ${v.modello}` }))} />
          {comm
            ? <PianoFields piano={f.piano} onPick={pickPiano} onName={set("piano")} />
            : <DurataConformita value={f.durata_mesi} onChange={set("durata_mesi")} />}
          <TextField label="Data inizio *" type="date" value={f.data_inizio} onChange={set("data_inizio")} testId="garanzia-data-inizio-input" />
          {comm && <TextField label="Durata (mesi)" type="number" min={1} value={f.durata_mesi} onChange={set("durata_mesi")} testId="garanzia-durata-input" />}
          <TextField label="Km alla consegna" type="number" value={f.km_inizio} onChange={set("km_inizio")} testId="garanzia-km-inizio-input" />
          {comm && <CommercialeFields f={f} set={set} />}
          <ComponentiPicker selected={f.componenti} onToggle={toggleComp} />
          <Field label="Note / condizioni particolari" className="sm:col-span-2">
            <Textarea value={f.note} onChange={(e) => set("note")(e.target.value)} data-testid="garanzia-note-input" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="garanzia-cancel-btn">Annulla</Button>
          <Button onClick={save} disabled={!f.cliente_id || !f.veicolo_id || !f.data_inizio} className="bg-[#B08D57] hover:bg-[#96764A]" data-testid="garanzia-save-btn">Salva garanzia</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
